import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import { Button, Card, Col, Row } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FieldValues, useForm, UseFormGetValues } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { ParticipantMainForm } from './ParticipantMainForm'
import { ParticipantChildForm } from './ParticipantChildForm'
import { ParticipantCodeLoading } from './ParticipantCodeModal'
import { RootState } from '../../store'
import { addToast } from '../../store/misc/misc'
import { IChild } from '../../store/participant/interfaces'
import { ToastType } from '../../store/misc/interfaces'
import StageNavigation from '../shared/StageNavigation'
import * as routes from '../../routes'
import { setParticipant } from '../../store/participant/participant'
import { CustomAnswer, CustomQuestion } from '../../store/events/interfaces'
import { filterCustomQuestions, isSupportedCustomQuestion } from '../../utils/customQuestions'
import { fieldRequired, mainSchema } from './ParticipantSchemaDefinitions'
import useValidators from '../../hooks/validators'
import { setPaymentMethod } from '../../store/paymentData'
import { checkProperAge } from '../../functions/registrations/ageIntegrity'
import { checkProperGender } from '../../functions/registrations/genderIntegrity'
import { IInstitution } from '../../store/institution/interfaces'
import { useAppSelector, usePageTitle } from '../../hooks'
import { useCheckPaymentStageSkip, useNumPaymentsEnabled } from '../../functions/registrations/checkPaymentStageSkip'
import { validateAccessKeyAPI } from '../../functions/participant/accessKeyValidity'
import LoadedViaTokenIndicator from '../shared/LoadedViaTokenIndicator'
import { CustomQuestionToast } from '../registrations/RegistrationListItem/CustomQuestion'

type FormValuesMap = Map<number, UseFormGetValues<FieldValues>>

const ParticipantStage = () => {
  let navigate = useNavigate()
  const dispatch = useDispatch()
  const { addValidator, removeValidator, hasValidationErrors } = useValidators()
  const { data: program } = useAppSelector((state: RootState) => state.program)
  const { baseUrl, loadedViaCode, loadedViaFamilyToken } = useAppSelector((state: RootState) => state.misc)
  const { list: registrations } = useAppSelector(
    (state: RootState) => state.registrations,
  )
  const { data: institution } = useAppSelector(
    (state: RootState) => state.institution,
  )
  const [subFormValues, setSubFormValues] = useState<FormValuesMap>(new Map())

  const initialChildren = useAppSelector((state) => state.participant.data.children)

  const [children, setChildren] = useState<IChild[]>(initialChildren || [])

  const skipPaymentStage = useCheckPaymentStageSkip()
  const anyPaymentEnabled = useNumPaymentsEnabled() > 0

  filterCustomQuestions(program.participant_questions).forEach((question: CustomQuestion) => {
    mainSchema[`custom_answer$${question.id}`] = question.required
      ? yup.string().required(fieldRequired)
      : yup.string()
  })

// update the access_key validation based on the access_key_mode on the program
  mainSchema['access_key'] = program.access_key_mode === 'req' ?
    yup.string().required(fieldRequired).test(
      'check-access-key', // A unique name for this test
      'Der Zugangscode ist ungültig!', // Message to display if the test fails
      async function(value) {
        if (loadedViaCode) return true
        const isValid = await validateAccessKeyAPI(baseUrl, program.id, value, registrations.map(r => r.event.event_id))
        return isValid // Pass or fail based on the API result
      },
    )
    : program.access_key_mode === 'opt' ?
      yup.string().test(
        'check-optional-access-key',
        'Der Zugangscode ist ungültig!',
        async function(value) {
          if (!value) return true // If access_key is empty, do nothing
          if (loadedViaCode) return true
          const isValid = await validateAccessKeyAPI(baseUrl, program.id, value, registrations.map(r => r.event.event_id))
          return isValid // Pass or fail based on the API result
        },
      )
      : yup.string()

  mainSchema['gender'] = program.gender_selection_needed ? yup.string().required(fieldRequired) : yup.string()

  const { data: participant } = useAppSelector(
    (state: RootState) => state.participant,
  )
  usePageTitle('Persönliche Angaben')

  useEffect(() => {
    if (loadedViaFamilyToken) {
      dispatch(
        addToast(CustomQuestionToast)
      )
    }
  }, [loadedViaFamilyToken, dispatch])

  const isOnlyAdultEvents = () => {
    for (const reg of registrations) {
      if (!reg.event.is_only_for_adults) {
        return false
      }
    }
    return true
  }

  const createNewChild = () => {
    console.log('Creating new child!', children)
    return {
      id: children.length ? children[children.length - 1].id + 1 : 1,
      name: '',
      date_of_birth: '',
    } as IChild
  }

  const getSendingPostCheck = (institution: IInstitution) => {
    let sendingPostTest = (target: string) => {
      return true
    }
    if (
      'participant-stage' in institution.custom_configuration &&
      'behavior' in institution.custom_configuration['participant-stage'] &&
      institution.custom_configuration['participant-stage']['behavior'] ===
      'custom-school-select'
    ) {
      sendingPostTest = (target: string) => {
        const receivers =
          institution.custom_configuration['participant-stage']['data'][
          'receivers'
          ]
        return receivers.some(
          (sending_post: { name: string; email: string; address: string }) =>
            sending_post.name === target,
        )
      }
    }
    return mainSchema.sending_post.test(
      'validChoices',
      'Bitte wählen Sie eine der gegebenen Optionen aus.',
      sendingPostTest,
    )
  }

  const validationSchema = yup.object().shape({
    ...mainSchema,
    gender: program.gender_selection_needed ? mainSchema.gender.test({
      name: 'proper_gender_check',
      test: function (value: string) {
        const res = checkProperGender(value, registrations)
        return res.ok && !res.message
          ? true
          : this.createError({ path: 'gender', message: res.message })
      },
    }) : yup.string(),
    date_of_birth: mainSchema.date_of_birth.test({
      name: 'proper_age_check',
      test: function (value: string) {
        if (program.requires_family_data) {
          // Skip age checking on the parent in family registration
          return true
        }
        const res = checkProperAge(
          value,
          registrations,
          program.age_check_tolerance,
        )
        return res.ok && !res.message
          ? true
          : this.createError({ path: 'date_of_birth', message: res.message })
      },
    }),
    zip_code: mainSchema.zip_code
      .test({
        name: 'custom_module_fulda_check',
        test: function (zip: string) {
          const setZipCode =
            'participant-stage' in institution.custom_configuration &&
            'behavior' in
            institution.custom_configuration['participant-stage'] &&
            institution.custom_configuration['participant-stage'][
            'behavior'
            ] === 'validate-zipcode-with-categories'
          if (setZipCode) {
            const mapping =
              institution.custom_configuration['participant-stage']['data'][
              'mapping'
              ]
            const requiredCategory =
              zip in mapping ? mapping[zip] : mapping['*']
            const blockingEvents: string[] = []
            for (const reg of registrations) {
              if (reg.event.price_categories.length > 0) {
                const category = reg.event.price_categories.find(
                  price_cat => price_cat.name === requiredCategory,
                )
                if (
                  category &&
                  !category.has_free_places &&
                  category.is_waiting_list_full
                ) {
                  blockingEvents.push(`"${reg.event.name}"`)
                }
              }
            }
            if (blockingEvents.length > 0) {
              let message: string
              blockingEvents.length === 1
                ? (message = `In der Veranstaltung ${blockingEvents[0]} ist für Ihre Postleitzahl (Kategorie "${requiredCategory}") kein Platz mehr frei.`)
                : (message = `In den Veranstaltungen ${blockingEvents.join(
                  ', ',
                )} sind für Ihre Postleitzahl (Kategorie "${requiredCategory}") keine Plätze mehr frei.`)

              return this.createError({
                path: 'zip_code',
                message: message,
              })
            }
          }
          return true
        },
      })
      .test(
        'validChoices',
        'Bitte wählen Sie eine der gegebenen Optionen aus.',
        (val: string) =>
          !program.restrict_zip_codes ||
          program.restricted_zip_codes.some(zipcode => zipcode === val),
      ),
    sending_post: getSendingPostCheck(institution),
  })
  const { register, formState, formState: { errors }, getValues, setValue, trigger, reset } = useForm(
    {
      resolver: yupResolver(validationSchema),
      mode: 'onBlur',
    },
  )

  useEffect(() => {
    addValidator('participant', { trigger, formName: 'ParticipantForm' })
  }, [addValidator, trigger])

  // Works like default values -> reset to data from participant
  useEffect(() => {
    reset({ ...participant, email_repeat: participant.email })
  }, [participant, reset])


  const processForms = async () => {

    const childFormValueList = await Promise.all(
      Array.from(subFormValues.entries()).map(async ([id, gV]) => ({ id, ...(await gV()) })),
    )

    const min_children_amount = Math.max(
      ...registrations.map(reg => reg.numberOfChildren),
    )

    if (
      // when loaded via code, check for loaded amount of children, otherwise against the newly entered amount
      (loadedViaCode && participant.number_of_children < min_children_amount) ||
      (!loadedViaCode && program.requires_family_data && childFormValueList.length < min_children_amount)
    ) {
      dispatch(
        addToast({
          body: loadedViaCode ? `Sie können maximal ${participant.number_of_children} Kinder gleichzeitig zu Veranstaltungen anmelden. Korrigieren Sie die Anzahl bitte im vorherigen Schritt.`: `Sie müssen mindestens ${min_children_amount} Kind${min_children_amount >= 2 ? 'er' : ''} angeben`,
          title: 'Formularfehler',
          type: ToastType.Danger,
        }),
      )
      return false
    }
    const validationErrors = await hasValidationErrors()
    if (Object.keys(errors).length > 0 || validationErrors) {
      console.table(errors)
      dispatch(
        addToast({
          body: 'Das Formular enthält noch Fehler.',
          title: 'Formularfehler',
          type: ToastType.Danger,
        }),
      )
      return false
    } else {
      const newParticipant = getValues()
      if (newParticipant.sending_post) {
        newParticipant.school = newParticipant.sending_post
      }
      program.agreement_html && (newParticipant.newsletter_allowed = false)

      const customAnswers: CustomAnswer[] = []
      const answers: string[] = Object.keys(
        newParticipant,
      ).filter((key: string) => key.startsWith('custom_answer$'))

      answers.forEach(answer => {
        const id: string = answer.split('$')[1]
        const customQuestion: CustomQuestion = program.participant_questions.find(
          question => parseInt(id) === question.id && isSupportedCustomQuestion(question),
        ) as CustomQuestion
        if (customQuestion) {
          customAnswers.push({
            question_id: parseInt(id),
            question_label: customQuestion.label,
            question_type: customQuestion.type,
            question_description: customQuestion.description,
            answer_required: customQuestion.required,
            answer_choices: customQuestion.choices,
            answer_text: newParticipant[answer],
            question_scope: customQuestion.scope,
          })
        }
      })
      newParticipant.custom_answers = customAnswers
      newParticipant.token = participant.token
      // Persist children from subforms and keep their original IDs
      const updatedChildren = childFormValueList.filter(
        list => Object.keys(list).length > 1,
      )
      newParticipant.children = updatedChildren
      newParticipant.originalRegistrationIds = participant.originalRegistrationIds

      dispatch(setParticipant(newParticipant))
      // Only one of these can be active at any time. If both are off, the formValueList is empty
      
      console.log('updatedChildren:', updatedChildren)
      return true
    }
  }

  return (
    <>
      <Card>
        <Card.Body>
          <Card.Title>
            <h2 aria-level={1}>
              Persönliche Angaben
            </h2>
            <LoadedViaTokenIndicator />
          </Card.Title>
          <Card.Text>
            {!program.requires_family_data ? (
              <>
                Weitere Familienmitglieder können am Ende der Anmeldung oder über den Veranstaltungspass hinzugefügt werden.
              </>
              ):<></>
            }
          </Card.Text>
          <Card.Text>Pflichtfelder sind mit einem * markiert.</Card.Text>
        </Card.Body>
        {program.access_key_mode !== 'off' &&
          program.loading_via_access_keys_possible && !loadedViaCode && (
            <Card.Footer>
              <ParticipantCodeLoading />
            </Card.Footer>
          )}
      </Card>
      <Card className='mt-3'>
        <Card.Body>
          <Row>
            <Col
              md={
                program.requires_family_data && !isOnlyAdultEvents()
                  ? 6
                  : 12
              }
              sm={12}
            >
              <ParticipantMainForm
                register={register}
                getValues={getValues}
                setValue={setValue}
                trigger={trigger}
                errors={errors}
                formState={formState}
                participant={participant}
              />
            </Col>
            {program.requires_family_data && !isOnlyAdultEvents() && (
                <Col md={6} sm={12}>
                  { loadedViaCode ?
                    <>
                      <h5>Daten Ihrer Kinder</h5>
                      <p>
                        Sie haben Daten von {participant.number_of_children} Kindern angegeben.
                      </p>
                    </>
                    :
                    <>
                      <h5>Daten Ihrer Kinder ({children.length})</h5>
                      <Button
                        variant='primary'
                        onClick={() => setChildren([...children, createNewChild()])}
                        className='btn mobile-btn mb-2'
                      >
                        Kind hinzufügen
                      </Button>
                      {children ? (
                        children.map(child => {
                          return (
                            <div key={child.id}>
                              <ParticipantChildForm
                                child={child}
                                onCreateForm={
                                  (formValue, validator) => {
                                    setSubFormValues(formValues => {
                                      formValues.set(child.id, formValue)
                                      return formValues
                                    })
                                    addValidator(`child-${child.id}`, validator)
                                  }
                                }
                                onRemoveForm={
                                  () => {
                                    // delete from child form values map
                                    setSubFormValues(formValues => {
                                      formValues.delete(child.id)
                                      return formValues
                                    })
                                    // remove the validators
                                    removeValidator(`child-${child.id}`)
                                    // delete from local state
                                    setChildren(
                                      children.filter((c) => c.id !== child.id),
                                    )
                                  }
                                }
                              />
                              <br />
                            </div>
                          )
                        })
                      ) : (
                        <></>
                      )}
                    </>
                  }
                </Col>
              )}
          </Row>
        </Card.Body>
      </Card>
      <StageNavigation
        backward={{
          text: 'Zurück',
          handler: () => navigate(routes.REGISTRATIONS.path),
        }}
        forward={
          skipPaymentStage
            ? {
              text: routes.VERIFY.longName,
              handler: () =>
                processForms().then(redirect => {
                  if (redirect) {
                    dispatch(setPaymentMethod('free'));
                    // if the customer doesn't provide any payment ever anyways, this toast is useless
                    if (anyPaymentEnabled){
                      dispatch(
                        addToast({
                          title: 'Info',
                          body: 'Der Bezahlprozess wurde übersprungen, da die Auswahl nur kostenlose Veranstaltungen enthält.',
                          type: ToastType.Info,
                        })
                      );
                    }
                    navigate(routes.VERIFY.path);
                  }
                }),
            }
            : {
              text: routes.PAYMENT.longName,
              handler: () =>
                processForms().then(redirect => {
                  if (redirect) {
                    navigate(routes.PAYMENT.path);
                  }
                }),
            }
        }
      />
    </>
  )
}

export default ParticipantStage
